import React, { useEffect, useRef, useState } from 'react';
import { Resolver, useForm } from 'react-hook-form';
import { IFormElementOptionProp, initialOrgLegalEntityFormValues } from 'models/form';
import { yupResolver } from '@hookform/resolvers/yup';
import Grid from '@mui/material/Grid';
import { FormInput } from 'components/molecules/FormInput';
import Button from 'components/atoms/Button';
import { EButtonVariants } from 'constants/Buttons';
import { FormSelect } from 'components/molecules/FormSelect';
import { FormRadioGroup } from 'components/molecules/FormRadioGroup';
import { useLegalEntities } from 'pages/LegalEntitiesPage/hooks';
import { Box, Typography } from '@mui/material';
import { mapDictionaryToOptionProp } from 'helpers/dictionary';
import { IOrgLegalEntityData } from 'components/organisms/OrgLegalEntityDetails';
import { useTranslations } from 'hooks/useTranslations';
import { useOrgLEFormSchema } from 'pages/LegalEntitiesPage/schemaValidation';
import { EDictionaryTypes, IDictionary } from 'models/dictionary';
import { useLang } from 'models/langContext';
import { ECustomFieldObjectType, ECustomFieldType } from 'models/customField';
import FormCustomField from 'components/organisms/FormCustomField';
import { ICustomFieldForm } from 'pages/LegalEntitiesPage/IndividualSearchForm';
import { ActionTypes } from 'state/actions/alert';
import { Alert, EAlertVariants } from 'components/atoms/Alert';
import { useAlert } from 'models/alertContext';
import { formatDateTimeToIso } from 'utils/date';
import { arraysEqual } from 'utils/form';
import { useScroll } from 'hooks/useScroll';
import { usePermission } from 'hooks/usePermission';
import { EPermission } from 'models/permissions';
import { ELegalVisibility } from 'constants/LegalVisibility';
import { EOrganisationCategory } from 'constants/OrganisationCategory';
import { useWeaponLicenceModule } from 'hooks/useWeaponLicenceModule';
import { useAccount } from 'pages/AccountPage/hooks';
import { sortBy } from 'helpers/licences';
import { FormMultiSelect } from 'components/molecules/FormMultiSelect';
import { SubsectionHeader } from '../LegalEntityForm/styles';
import { useSystemFieldsConfiguration } from 'pages/SystemSettingsPage/SystemFieldsConfigurationPage/hooks';
import { LEModuleTypes } from 'constants/LegalEntityTypes';
import { EFieldsOptional } from 'pages/SystemSettingsPage/SystemFieldsConfigurationPage/helpers';

export interface IOrgLegalEntityFormValues {
  uniqueId: string;
  organizationName: string;
  organisationTypeUniqueIds: string[];
  organisationCategoryUniqueId: string;
  departmentName: string | null;
  description: string | null;
  visibilityUniqueId: string;
  city: string;
  primaryWeaponLicencingAuthorityUniqueId: string;
  secondaryWeaponLicencingAuthorityUniqueId: string;
  customFields?: ICustomFieldForm[];
}

export interface ILegalEntityForm {
  onCancel?: () => void;
  onSubmit: (arg: IOrgLegalEntityFormValues) => void;
  initialFormValues?: IOrgLegalEntityData | null;
  isEdit?: boolean;
  isDisabled?: boolean;
}

const OrgLegalEntityForm = ({
  onCancel,
  onSubmit,
  initialFormValues,
  isEdit = false,
  isDisabled = false,
}: ILegalEntityForm) => {
  const {
    loadDictionaries,
    legalVisibility,
    weaponLicencingAuthority,
    organisationStateType,
    organisationCivilianType,
    organisationMilitaryType,
    organisationPoliceType,
    organisationCategory,
    getCustomField,
    customFieldsOLE,
  } = useLegalEntities();
  const { hasPermissions } = usePermission();
  const [isSubmitLoading, setIsSubmitLoading] = useState<boolean>(false);
  const [optionsVisibility, setOptionsVisibility] = useState<
    IFormElementOptionProp[] | undefined
  >(undefined);
  const [noLA, setNoLA] = useState(false);
  const { hasWeaponLicenceModule } = useWeaponLicenceModule();
  const [isDisableVisibility, setIsDisableVisibility] = useState<boolean>(true);
  const { account, getSelfAccountData } = useAccount();
  const { t } = useTranslations();
  const { selectedLanguage } = useLang();
  const { setAlert } = useAlert();
  const { elRef, scrollToError } = useScroll();
  const [matchingOrganisationCategories, setMatchingOrganisationCategories] = useState<
    IDictionary[]
  >([]);
  let wasCustomFieldsDataChanged = false;
  const {
    getFieldsOptional,
    fieldsOptional,
    prepareFormByFieldsOptional,
    hiddenFieldsName,
    requiredFieldsName,
  } = useSystemFieldsConfiguration();
  const { schema } = useOrgLEFormSchema(
    customFieldsOLE,
    hiddenFieldsName,
    requiredFieldsName,
    !!hasWeaponLicenceModule(),
    initialFormValues?.customFields,
  );

  useEffect(() => {
    getFieldsOptional([LEModuleTypes.organisation]);
  }, []);

  useEffect(() => {
    if (fieldsOptional.length) {
      prepareFormByFieldsOptional(fieldsOptional, setValue);
    }
  }, [fieldsOptional]);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
    watch,
  } = useForm<IOrgLegalEntityData | IOrgLegalEntityFormValues>({
    defaultValues: initialFormValues || initialOrgLegalEntityFormValues,
    resolver: yupResolver(schema) as unknown as Resolver<
      IOrgLegalEntityData | IOrgLegalEntityFormValues
    >,
  });

  const watchOrganisationCategory = watch('organisationCategoryUniqueId');
  const [organizationType, setOrganizationType] = useState<IDictionary[] | null>(null);
  const [organizationTypeName, setOrganizationTypeName] = useState<string>('');
  const previousCategoryRef = useRef<string | undefined>(undefined);

  const getOrganizationTypeByCategory = (categoryId: string) => {
    switch (categoryId) {
      case EOrganisationCategory.civilian:
        return organisationCivilianType;
      case EOrganisationCategory.state:
        return organisationStateType;
      case EOrganisationCategory.military:
        return organisationMilitaryType;
      case EOrganisationCategory.police:
        return organisationPoliceType;
      default:
        return null;
    }
  };

  useEffect(() => {
    if (account && organisationCategory)
      setMatchingOrganisationCategories(
        organisationCategory.filter((it) =>
          account.organisationCategoryIds.includes(it.uniqueId as string),
        ),
      );
  }, [account, organisationCategory]);

  useEffect(() => {
    if (
      !watchOrganisationCategory &&
      matchingOrganisationCategories &&
      matchingOrganisationCategories.length
    ) {
      setValue(
        'organisationCategoryUniqueId',
        matchingOrganisationCategories[0].uniqueId.toString(),
      );
    }
  }, [matchingOrganisationCategories]);

  useEffect(() => {
    if (!watchOrganisationCategory) {
      setValue('organisationTypeUniqueIds', []);
      setOrganizationType(null);
      previousCategoryRef.current = watchOrganisationCategory;
    } else {
      const type: IDictionary[] | null = getOrganizationTypeByCategory(
        watchOrganisationCategory,
      );
      setOrganizationType(type);
      setOrganizationTypeName(
        matchingOrganisationCategories.find(
          (item) => item.uniqueId === watchOrganisationCategory,
        )?.name ?? '',
      );

      // prevent clearing on initial load / when reset() is called with the same category
      if (
        previousCategoryRef.current !== undefined &&
        previousCategoryRef.current !== watchOrganisationCategory
      ) {
        setValue('organisationTypeUniqueIds', []);
      }

      previousCategoryRef.current = watchOrganisationCategory;
    }
  }, [
    watchOrganisationCategory,
    organisationCivilianType,
    organisationStateType,
    organisationMilitaryType,
    organisationPoliceType,
  ]);

  useEffect(() => {
    reset(initialFormValues || initialOrgLegalEntityFormValues);
  }, [reset, initialFormValues]);

  useEffect(() => {
    if (initialFormValues?.uniqueId) {
      setValue('city', 'default');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFormValues]);

  useEffect(() => {
    getSelfAccountData();
  }, []);

  useEffect(() => {
    (async () => {
      scrollToError(errors);
    })();
  }, [errors]);

  useEffect(() => {
    if (legalVisibility) {
      setOptionsVisibility(
        mapDictionaryToOptionProp(legalVisibility.concat().sort(sortBy('name'))),
      );
    }
  }, [legalVisibility]);

  useEffect(() => {
    if (
      account &&
      account.licenceAuthorityIds.length === 1 &&
      !isEdit &&
      hasWeaponLicenceModule()
    ) {
      setValue('primaryWeaponLicencingAuthorityUniqueId', account.licenceAuthorityIds[0]);
    }
  }, [account]);

  useEffect(() => {
    if (
      watchOrganisationCategory === EOrganisationCategory.civilian &&
      account &&
      !account.licenceAuthorityIds.length &&
      hasWeaponLicenceModule()
    ) {
      setNoLA(true);
    } else {
      setNoLA(false);
    }
  }, [account, watchOrganisationCategory]);

  useEffect(() => {
    if (optionsVisibility && !hasPermissions([EPermission.VIEW_ALL_LE])) {
      if (!isEdit) {
        setValue('visibilityUniqueId', ELegalVisibility.PUBLIC as string);
      }
      setIsDisableVisibility(true);
    } else {
      setIsDisableVisibility(false);
    }
  }, [initialFormValues, isEdit, optionsVisibility]);

  const hasDataChanged = (data: any): boolean => {
    let hasChanged = false;

    if (
      initialFormValues?.description !== data.description ||
      initialFormValues?.departmentName !== data.departmentName ||
      initialFormValues?.organizationName !== data.organizationName ||
      initialFormValues?.visibilityUniqueId !== data.visibilityUniqueId ||
      initialFormValues?.organisationCategoryUniqueId !== data.organisationCategoryUniqueId ||
      !arraysEqual(
        initialFormValues?.organisationTypeUniqueIds,
        data.organisationTypeUniqueIds,
      ) ||
      initialFormValues?.primaryWeaponLicencingAuthorityUniqueId !==
        data.primaryWeaponLicencingAuthorityUniqueId ||
      initialFormValues?.secondaryWeaponLicencingAuthorityUniqueId !==
        data.secondaryWeaponLicencingAuthorityUniqueId
    ) {
      hasChanged = true;
    }

    customFieldsOLE.forEach((customField) => {
      let matchingDataCustomFieldValue;
      // eslint-disable-next-line default-case
      switch (customField.customFieldType) {
        case ECustomFieldType.DATE: {
          const value = data[customField.name];
          if (value !== undefined && value !== null) {
            matchingDataCustomFieldValue = formatDateTimeToIso(value);
          }
          break;
        }
        case ECustomFieldType.BOOL: {
          const value = data[customField.name];
          if (value !== undefined && value !== null && value.trim() !== '') {
            matchingDataCustomFieldValue = data[customField.name] === '1';
          }
          break;
        }
        case ECustomFieldType.TEXT: {
          const value = data[customField.name];
          if (value !== undefined && value !== null && value.trim() !== '') {
            matchingDataCustomFieldValue = data[customField.name];
          }
          break;
        }
      }
      let initialCustomField;
      let initCustomFieldData;
      if (!!initialFormValues && initialFormValues.customFields !== undefined) {
        initialCustomField = initialFormValues.customFields.find(
          (initCustomField) => initCustomField.customFieldUniqueId === customField.uniqueId,
        );
        initCustomFieldData =
          initialCustomField &&
          initialCustomField.value !== undefined &&
          initialCustomField.value !== null
            ? initialCustomField.value
            : undefined;
      }
      if (initCustomFieldData !== matchingDataCustomFieldValue) {
        wasCustomFieldsDataChanged = true;
        hasChanged = true;
      }
    });

    return hasChanged;
  };

  const onSubmitForm = async (data: any) => {
    if (!hasDataChanged(data) && initialFormValues) {
      setAlert(ActionTypes.SET_SAME_PAGE_ALERT, {
        text: t('general.noChanged.warning.message'),
        variant: EAlertVariants.warning,
      });
      return;
    }

    if (wasCustomFieldsDataChanged) {
      data.customFields = [];

      const customFieldPayload: ICustomFieldForm[] = [];

      Object.keys(data).map((key) => {
        const customField = customFieldsOLE.find((item) => item.name === key);
        if (customField) {
          const customFieldItem: ICustomFieldForm = {
            customFieldUniqueId: customField.uniqueId,
          };
          if (customField.customFieldType === ECustomFieldType.TEXT) {
            if (data[key] === undefined || data[key] === null) {
              customFieldItem.text = undefined;
            } else if (data[key].trim() === '') {
              customFieldItem.text = undefined;
            } else {
              customFieldItem.text = data[key];
            }
          } else if (customField.customFieldType === ECustomFieldType.BOOL) {
            if (data[key] === '') {
              customFieldItem.bool = null;
            } else {
              customFieldItem.bool = data[key] === '1';
            }
          } else if (customField.customFieldType === ECustomFieldType.DATE) {
            customFieldItem.date = data[key] ? formatDateTimeToIso(data[key]) : null;
          }
          customFieldPayload.push(customFieldItem);
          delete data[key];
        }
        return false;
      });

      if (customFieldPayload?.length && customFieldPayload.length > 0) {
        data.customFields = customFieldPayload;
      } else {
        delete data.customFields;
      }
    } else {
      delete data.customFields;
    }

    if (watchOrganisationCategory !== EOrganisationCategory.civilian) {
      delete data.primaryWeaponLicencingAuthorityUniqueId;
      delete data.secondaryWeaponLicencingAuthorityUniqueId;
    }

    setIsSubmitLoading(true);
    if (initialFormValues) {
      await onSubmit({
        ...data,
      });
      setIsSubmitLoading(false);
    } else {
      await onSubmit(data);
      setIsSubmitLoading(false);
    }
    setIsSubmitLoading(false);
  };

  useEffect(() => {
    (async function init() {
      await loadDictionaries([
        EDictionaryTypes.LegalVisibility,
        EDictionaryTypes.OrganisationCivilianType,
        EDictionaryTypes.OrganisationStateType,
        EDictionaryTypes.OrganisationMilitaryType,
        EDictionaryTypes.OrganisationPoliceType,
        EDictionaryTypes.OrganisationCategory,
        EDictionaryTypes.weaponLicencingAuthority,
      ]);
      await getCustomField(ECustomFieldObjectType.LE_ORGANIZATION);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage]);

  return (
    <>
      {noLA && (
        <Box sx={{ mb: 2 }}>
          <Alert
            text={t('legalEntity.add.userMissingLicenceAuthority.message')}
            variant={EAlertVariants.warning}
          />
        </Box>
      )}

      <form onSubmit={handleSubmit(onSubmitForm)}>
        <Grid container columnSpacing={2.5} ref={elRef}>
          <Grid size={{ xs: 12 }}>
            <SubsectionHeader variant="h5">
              {t('editLegalEntity.subsectionTitle.generalDetails')}
            </SubsectionHeader>
          </Grid>
          <Grid container size={{ xs: 12 }} spacing={3}>
            <Grid size={{ xs: 12, lg: 4 }}>
              <FormRadioGroup
                name="organisationCategoryUniqueId"
                label={`${t('editLegalEntity.organisationCategoryUniqueId.label')}*`}
                options={mapDictionaryToOptionProp(matchingOrganisationCategories)}
                control={control}
                errors={errors}
                disabled={isEdit}
                withValidation
              />
            </Grid>
            <Grid size={{ xs: 12, lg: 4 }}>
              <FormRadioGroup
                name="visibilityUniqueId"
                label={`${t('editLegalEntity.visibility.label')}*`}
                options={optionsVisibility ?? []}
                control={control}
                errors={errors}
                disabled={isDisableVisibility}
                withValidation
              />
            </Grid>
          </Grid>
          <Grid size={{ xs: 12, lg: 4 }}>
            <FormMultiSelect
              options={mapDictionaryToOptionProp(organizationType)}
              name="organisationTypeUniqueIds"
              label={`${organizationTypeName} ${t('editLegalEntity.organizationType.label')}*`}
              setValue={setValue}
              control={control}
              errors={errors}
              disabled={!watchOrganisationCategory || !organizationType}
            />
          </Grid>
          <Grid size={{ xs: 12, lg: 4 }}>
            <FormInput
              name="organizationName"
              label={`${t('editLegalEntity.name.label')}*`}
              control={control}
              setValue={setValue}
              errors={errors}
              charsLimit={80}
            />
          </Grid>
          {!hiddenFieldsName.includes(EFieldsOptional.departmentName) && (
            <Grid size={{ xs: 12, lg: 4 }}>
              <FormInput
                name="departmentName"
                label={t('editLegalEntity.departmentName.label')}
                setValue={setValue}
                control={control}
                errors={errors}
                charsLimit={80}
                required={requiredFieldsName.includes(EFieldsOptional.departmentName)}
              />
            </Grid>
          )}
          <Grid size={{ xs: 12 }}>
            <FormInput
              name="description"
              label={t('editLegalEntity.description.label')}
              setValue={setValue}
              control={control}
              errors={errors}
              charsLimit={250}
            />
          </Grid>
          {customFieldsOLE.map((customField) => (
            <Grid size={{ xs: 12, lg: 4 }} key={customField.uniqueId}>
              <FormCustomField
                data={customField}
                control={control}
                errors={errors}
                setValue={setValue}
              />
            </Grid>
          ))}
          {watchOrganisationCategory === EOrganisationCategory.civilian &&
            hasWeaponLicenceModule() && (
              <>
                <Grid size={{ xs: 12 }}>
                  <SubsectionHeader variant="h5">
                    {t('editLegalEntity.subsectionTitle.licencingAuthorities')}
                  </SubsectionHeader>
                </Grid>
                <Grid size={{ xs: 12, lg: 4 }}>
                  <FormSelect
                    options={mapDictionaryToOptionProp(weaponLicencingAuthority)}
                    setValue={setValue}
                    name="primaryWeaponLicencingAuthorityUniqueId"
                    label={`${t(
                      'editLegalEntity.primaryWeaponLicencingAuthorityUniqueId.label',
                    )}*`}
                    control={control}
                    errors={errors}
                    disabled={
                      isDisabled || (!isEdit && account?.licenceAuthorityIds.length === 1)
                    }
                    canBeEmpty={false}
                  />
                </Grid>
                <Grid size={{ xs: 12, lg: 4 }}>
                  <FormSelect
                    options={mapDictionaryToOptionProp(weaponLicencingAuthority)}
                    setValue={setValue}
                    name="secondaryWeaponLicencingAuthorityUniqueId"
                    label={t(
                      'editLegalEntity.secondaryWeaponLicencingAuthorityUniqueId.label',
                    )}
                    control={control}
                    errors={errors}
                    disabled={isDisabled}
                  />
                </Grid>
              </>
            )}
          <Grid container size={{ xs: 12 }} spacing={2.5} sx={{ mt: { xs: 2, lg: 10 } }}>
            <Grid size={{ xs: 12, lg: 4 }}>
              <Button
                type="submit"
                fullWidth
                label={t('general.save.button')}
                id="legal_entity-save"
                variant={EButtonVariants.contained}
                isLoading={isSubmitLoading}
                disabled={noLA}
              />
            </Grid>

            {onCancel && (
              <Grid size={{ xs: 12, lg: 4 }}>
                <Button
                  fullWidth
                  label={t('general.cancel.button')}
                  id="legal_entity-cancel"
                  variant={EButtonVariants.outlined}
                  onClick={onCancel}
                />
              </Grid>
            )}
          </Grid>
          <Grid size={{ xs: 12 }} sx={{ mt: 2 }}>
            <Typography variant="caption">{t('general.mandatoryField.text')}</Typography>
          </Grid>
        </Grid>
      </form>
    </>
  );
};

export { OrgLegalEntityForm };
